<script setup lang="ts">
import {User} from "@/types";
import {computed, ref, watch} from "vue";
import {router, useForm} from "@inertiajs/vue3";
import ErrorAlert from "@/components/alerts/ErrorAlert.vue";
import TextField from "@/components/forms/TextField.vue";

interface Props {
    user: User
}

const props = defineProps<Props>()

const isDialogOpen = ref(false)
const isLoading = ref(false)

const form = useForm({
    name: '',
    confirmation: ''
})

watch(isDialogOpen, (isOpen) => {
    if (isOpen) {
        form.reset()
    }
})

const isConfirmed = computed(() => form.name === props.user.name && form.confirmation === 'delete my account')

const remove = () => {
    if (!isConfirmed.value) {
        return
    }

    router.delete(route('settings.account.destroy'), {
        onStart: () => {
            isLoading.value = true
        },
        onFinish: () => {
            isLoading.value = false
        },
        onSuccess: () => {
            isDialogOpen.value = false
        }
    })
}
</script>

<template>
    <v-dialog
        v-model="isDialogOpen"
        max-width="500"
        overlay-opacity="0.4"
    >
        <template #activator="{ props: attrs }">
            <default-btn
                v-bind="attrs"
                class="border-sm text-error mr-5 d-block mt-3 custom-error"
                rounded="lg"
            >
                Delete your account
            </default-btn>
        </template>

        <v-card
            :flat="true"
            rounded="lg"
        >
            <v-card-title class="font-weight-bold border-bottom text-body-2 pt-4">
                Are yu sure you want to do this?
                <v-btn
                    variant="plain"
                    size="small"
                    icon="mdi-close"
                    class="float-end pb-2"
                    @click="isDialogOpen = false"
                />
            </v-card-title>
            <error-alert class="rounded-0">
                <v-icon color="red-lighten-2 mr-3">
                    mdi-alert-outline
                </v-icon> This is extremely important.
            </error-alert>
            <v-card-text class="pt-3 text-black text-body-2">
                <p class="mb-3">
                    We will <b>immediately delete all your projects</b>, along with all your transfers.
                </p>
                <p class="mb-3">
                    You will no longer be able to access your account, and after 90 days all your data will be permanently deleted.
                </p>
                <p class="mb-3">
                    For more help, read "<a
                        href="#"
                        class="text-primary"
                    >Deleting your user account</a>"
                </p>
                <v-divider class="my-5" />
                <text-field
                    v-model="form.name"
                    label="Your username"
                    :hide-details="true"
                    field-class="mb-3"
                />
                <text-field
                    v-model="form.confirmation"
                    :hide-details="true"
                >
                    <template #label>
                        To confirm, type <i class="font-weight-regular">delete my account</i> below:
                    </template>
                </text-field>
            </v-card-text>

            <v-card-actions class="px-5 py-4">
                <default-btn
                    class="border custom-error"
                    :class="!isConfirmed ? '' : 'text-error'"
                    :color="!isConfirmed ? null : 'grey-lighten-4'"
                    rounded="lg"
                    :block="true"
                    :loading="isLoading"
                    :disabled="!isConfirmed"
                    @click="remove"
                >
                    Delete my account
                </default-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<style scoped>
:deep(.v-dialog--active) {
    border-radius: 16px !important;
}

.border-bottom {
    border-bottom: 1px solid rgba(0, 0, 0, 0.12) !important;
}

.custom-error:hover {
    background-color: rgb(176, 0, 32) !important;
    color: white !important;
}
</style>
